/**
 * IN-GAME UI THEME — the runtime-editable color scheme for the game's HUD/menus/buttons (separate from the
 * marketing-site theme in siteTheme.ts). Stored under the live-tuning key `gameTheme`, applied as `--game-*`
 * CSS variables on :root. The in-game theme editor writes it; a "copy test → live" promote mirrors the site's.
 *
 * The DEFAULTS below are seeded from the website palette (cool-brown + emerald + brass) so the game chrome
 * matches the site out of the box; the built-in :root fallbacks in styles.css must stay in sync with these.
 */

import { tuningKey, onTuning } from "./tuningClient";

export interface GameTheme {
  panel: string;       // menubar / floating panel background (translucent dark)
  panelAlpha: number;  // 0..1 opacity of the panel background
  button: string;      // resting button fill (on the panel)
  buttonHover: string; // button hover fill
  ink: string;         // button/panel text
  inkDim: string;      // muted text (hints, subtitles)
  border: string;      // panel/button hairline border
  accent: string;      // primary accent — active screen tab, emphasis (emerald)
  accent2: string;     // secondary accent — active tool button (brass)
  danger: string;      // exit / destructive (warm red)
  radius: number;      // px — button/panel corner radius
  // ── Modals / dialogs (equipment, shop, market, achievements, automation, cutscene, tackle, forecast …) ──
  modalBg: string;     // modal / dialog panel background
  modalInk: string;    // modal text
  modalBorder: string; // modal borders + dividers
  modalRadius: number; // px — modal corner radius
  scrim: string;       // the dim behind an open modal
  scrimAlpha: number;  // 0..1 opacity of that dim
  tabActive: string;   // the ACTIVE tab accent inside modals (highlight bar / selected text)
}

/** Seeded from the website palette so the game matches the site. */
export const DEFAULT_GAME_THEME: GameTheme = {
  panel: "#1c1712",        // dark cool-brown, translucent (see panelAlpha)
  panelAlpha: 0.9,
  button: "#2b2621",       // resting button fill
  buttonHover: "#37312a",
  ink: "#ece3d4",          // warm-cream text (matches site --site-fg)
  inkDim: "#b3a894",
  border: "#3d372f",
  accent: "#4fae86",       // emerald (site secondary accent)
  accent2: "#cba24f",      // brass (site primary accent)
  danger: "#e0705a",
  radius: 12,
  // modals seeded from the website surface palette (a touch lighter than the ground, warm-cream ink)
  modalBg: "#2b2621",      // matches site --site-surface tone
  modalInk: "#ece3d4",
  modalBorder: "#3d372f",
  modalRadius: 16,
  scrim: "#0d0b08",        // warm near-black dim
  scrimAlpha: 0.55,
  tabActive: "#4fae86",    // emerald — active tab highlight
};

/** Merge the admin-set `gameTheme` tuning (if any) OVER the source default. */
export function resolveGameTheme(): GameTheme {
  const o = tuningKey<Partial<GameTheme>>("gameTheme", {});
  return { ...DEFAULT_GAME_THEME, ...(o && typeof o === "object" ? o : {}) };
}

/** #rrggbb + alpha → rgba() (for the translucent panel fills). */
function hexA(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const n = h.length === 3 ? h.split("").map((x) => x + x).join("") : h;
  const r = parseInt(n.slice(0, 2), 16) || 0, g = parseInt(n.slice(2, 4), 16) || 0, b = parseInt(n.slice(4, 6), 16) || 0;
  return `rgba(${r},${g},${b},${a})`;
}

/** Apply an in-game theme to the document as `--game-*` CSS variables. Idempotent. */
export function applyGameTheme(t: GameTheme): void {
  const r = document.documentElement;
  const set = (k: string, v: string) => r.style.setProperty(k, v);
  set("--game-panel", hexA(t.panel, Math.max(0, Math.min(1, t.panelAlpha))));
  set("--game-panel-solid", t.panel);
  set("--game-button", t.button);
  set("--game-button-hover", t.buttonHover);
  set("--game-ink", t.ink);
  set("--game-ink-dim", t.inkDim);
  set("--game-border", t.border);
  set("--game-accent", t.accent);
  set("--game-accent-soft", hexA(t.accent, 0.16));
  set("--game-accent-2", t.accent2);
  set("--game-accent-2-soft", hexA(t.accent2, 0.16));
  set("--game-danger", t.danger);
  set("--game-danger-soft", hexA(t.danger, 0.16));
  set("--game-radius", `${t.radius}px`);
  // modal / dialog tokens
  set("--game-modal-bg", t.modalBg);
  set("--game-modal-ink", t.modalInk);
  set("--game-modal-border", t.modalBorder);
  set("--game-modal-radius", `${t.modalRadius}px`);
  set("--game-scrim", hexA(t.scrim, Math.max(0, Math.min(1, t.scrimAlpha))));
  set("--game-tab-active", t.tabActive);
  set("--game-tab-active-soft", hexA(t.tabActive, 0.16));
}

/** Boot hook: apply the resolved game theme now + re-apply on any live-tuning change. Returns an unsubscribe. */
export function startGameTheme(): () => void {
  applyGameTheme(resolveGameTheme());
  return onTuning(() => applyGameTheme(resolveGameTheme()));
}
